import { useQuery } from '@apollo/client';
import type { Issue_Bool_Exp } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetIssuesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';

/**
 * V2 / GraphQL-only helper (CUS-257).
 *
 * The `issue_assessment_audit` GraphQL type exposes no `departments` or `tags`
 * relationship, so the "Open Issues Over Time" widget cannot filter its audit
 * query by the dashboard's department/tag filters directly. Issues DO expose
 * both relationships, so resolve the selected department + tag types to the set
 * of issue Ids that carry them (ANDed, matching how the dashboard applies both
 * global filters); the widget then narrows the audit history by `ParentIssueId`.
 *
 * On V3 the audit read filters by department + tag types server-side (the tRPC
 * `getHistory` path), so callers skip this resolver when the `trpc` flag is on.
 * It is therefore intentionally GraphQL-only and lives under `src/hooks/**`, the
 * sanctioned home for the Apollo bridge layer.
 */
export const useGetIssueIdsByDashboardFilter = ({
  departmentTypeIds,
  tagTypeIds,
  isSkipped,
}: {
  departmentTypeIds: string[];
  tagTypeIds: string[];
  isSkipped?: boolean;
}) => {
  const hasDepartments = departmentTypeIds.length > 0;
  const hasTags = tagTypeIds.length > 0;

  const where: Issue_Bool_Exp = {
    ...(hasDepartments && {
      departments: { DepartmentTypeId: { _in: departmentTypeIds } },
    }),
    ...(hasTags && {
      tags: { TagTypeId: { _in: tagTypeIds } },
    }),
  };

  const { data, loading } = useQuery(GetIssuesDocument, {
    variables: { where },
    // No department/tag selected → nothing to resolve; the widget shows all
    // issues.
    skip: isSkipped || (!hasDepartments && !hasTags),
    fetchPolicy: 'cache-and-network',
  });

  const issueIds = useMemo(
    () => data?.issue.map((issue) => issue.Id) ?? [],
    [data]
  );

  return { issueIds, loading };
};
